import { useState, useEffect, useCallback } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import {
  Search, Plus, X, Loader2, AlertTriangle, Bell, Pencil, Trash2, Save,
  Play, Shield, Clock, Users, XCircle, ToggleLeft, ToggleRight,
  TrendingUp, CheckCircle,
} from 'lucide-react';
import { api } from '@/utils/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EscalationRule {
  id: string;
  name: string;
  triggerType: 'sla_breach' | 'pattern' | 'auto_close' | 'unassigned';
  conditions: Record<string, any>;
  action: 'escalate' | 'notify' | 'reassign' | 'auto_close';
  active: boolean;
  priority: number;
  createdAt?: string;
}

interface EscalationSummary {
  rulesActive: number;
  escalatedToday: number;
  autoClosedToday: number;
  pendingEscalations: number;
}

interface EvaluationResult {
  triggered: number;
  details: Array<{ ruleId: string; ruleName: string; ticketsAffected: number; action: string }>;
}

const TRIGGER_COLORS: Record<string, string> = {
  sla_breach: 'bg-red-50 text-red-700',
  pattern: 'bg-orange-50 text-orange-700',
  auto_close: 'bg-blue-50 text-blue-700',
  unassigned: 'bg-yellow-50 text-yellow-700',
};

const TRIGGER_LABELS: Record<string, string> = {
  sla_breach: 'SLA Breach',
  pattern: 'Pattern',
  auto_close: 'Auto Close',
  unassigned: 'Unassigned',
};

const ACTION_COLORS: Record<string, string> = {
  escalate: 'bg-red-50 text-red-700',
  notify: 'bg-blue-50 text-blue-700',
  reassign: 'bg-purple-50 text-purple-700',
  auto_close: 'bg-green-50 text-green-700',
};

const ACTION_LABELS: Record<string, string> = {
  escalate: 'Escalate',
  notify: 'Notify',
  reassign: 'Reassign',
  auto_close: 'Auto Close',
};

const ACTIONS = ['escalate', 'notify', 'reassign', 'auto_close'];
const TRIGGER_TYPES = ['sla_breach', 'pattern', 'auto_close', 'unassigned'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];

// ─── Conditions Summary ───────────────────────────────────────────────────────

function conditionsSummary(triggerType: string, conditions: Record<string, any>): string {
  switch (triggerType) {
    case 'sla_breach':
      return `Priority: ${conditions.priority || 'any'}, Max ${conditions.maxHours || '?'}h`;
    case 'pattern':
      return `${conditions.ticketCount || '?'} tickets in ${conditions.windowDays || '?'} days`;
    case 'auto_close':
      return `Resolved for ${conditions.resolvedHours || '?'}h`;
    case 'unassigned':
      return `Unassigned for ${conditions.maxHours || '?'}h`;
    default:
      return JSON.stringify(conditions);
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function EscalationRulesPage() {
  const [rules, setRules] = useState<EscalationRule[]>([]);
  const [summary, setSummary] = useState<EscalationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Evaluate
  const [evaluating, setEvaluating] = useState(false);
  const [evalResult, setEvalResult] = useState<EvaluationResult | null>(null);
  const [showEvalResult, setShowEvalResult] = useState(false);

  // Add Rule Modal
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    triggerType: 'sla_breach' as string,
    action: 'escalate' as string,
    priority: 1,
    // sla_breach conditions
    slaPriority: 'high',
    slaMaxHours: '',
    // pattern conditions
    ticketCount: '',
    windowDays: '',
    // auto_close conditions
    resolvedHours: '',
    // unassigned conditions
    unassignedMaxHours: '',
  });

  // Edit Rule Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    triggerType: 'sla_breach' as string,
    action: 'escalate' as string,
    priority: 1,
    slaPriority: 'high',
    slaMaxHours: '',
    ticketCount: '',
    windowDays: '',
    resolvedHours: '',
    unassignedMaxHours: '',
  });

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/service/escalation-rules');
      setRules(res.data.data?.escalationRules ?? res.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load escalation rules');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/service/escalation-summary');
      setSummary(res.data.data ?? null);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchSummary();
  }, [fetchRules, fetchSummary]);

  const buildConditions = (f: typeof form): Record<string, any> => {
    switch (f.triggerType) {
      case 'sla_breach':
        return { priority: f.slaPriority, maxHours: Number(f.slaMaxHours) || undefined };
      case 'pattern':
        return { ticketCount: Number(f.ticketCount) || undefined, windowDays: Number(f.windowDays) || undefined };
      case 'auto_close':
        return { resolvedHours: Number(f.resolvedHours) || undefined };
      case 'unassigned':
        return { maxHours: Number(f.unassignedMaxHours) || undefined };
      default:
        return {};
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/service/escalation-rules', {
        name: form.name,
        triggerType: form.triggerType,
        conditions: buildConditions(form),
        action: form.action,
        priority: Number(form.priority),
      });
      setShowModal(false);
      setForm({ name: '', triggerType: 'sla_breach', action: 'escalate', priority: 1, slaPriority: 'high', slaMaxHours: '', ticketCount: '', windowDays: '', resolvedHours: '', unassignedMaxHours: '' });
      fetchRules();
      fetchSummary();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create escalation rule');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (rule: EscalationRule) => {
    setEditForm({
      id: rule.id,
      name: rule.name,
      triggerType: rule.triggerType,
      action: rule.action,
      priority: rule.priority,
      slaPriority: rule.conditions?.priority || 'high',
      slaMaxHours: rule.conditions?.maxHours?.toString() || '',
      ticketCount: rule.conditions?.ticketCount?.toString() || '',
      windowDays: rule.conditions?.windowDays?.toString() || '',
      resolvedHours: rule.conditions?.resolvedHours?.toString() || '',
      unassignedMaxHours: rule.conditions?.maxHours?.toString() || '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    try {
      setEditSaving(true);
      await api.put(`/service/escalation-rules/${editForm.id}`, {
        name: editForm.name,
        triggerType: editForm.triggerType,
        conditions: buildConditions(editForm),
        action: editForm.action,
        priority: Number(editForm.priority),
      });
      setShowEditModal(false);
      fetchRules();
      fetchSummary();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update rule');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (rule: EscalationRule) => {
    if (!confirm(`Delete escalation rule "${rule.name}"?`)) return;
    try {
      await api.delete(`/service/escalation-rules/${rule.id}`);
      fetchRules();
      fetchSummary();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule: EscalationRule) => {
    try {
      await api.put(`/service/escalation-rules/${rule.id}`, {
        name: rule.name,
        triggerType: rule.triggerType,
        conditions: rule.conditions,
        action: rule.action,
        priority: rule.priority,
        active: !rule.active,
      });
      fetchRules();
      fetchSummary();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to toggle rule');
    }
  };

  const handleEvaluate = async () => {
    try {
      setEvaluating(true);
      const res = await api.post('/service/escalation-rules/evaluate');
      setEvalResult(res.data.data ?? { triggered: 0, details: [] });
      setShowEvalResult(true);
      fetchSummary();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to evaluate rules');
    } finally {
      setEvaluating(false);
    }
  };

  // Render conditions form based on trigger type
  const renderConditionsForm = (f: typeof form, setter: (v: any) => void) => {
    switch (f.triggerType) {
      case 'sla_breach':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
              <select value={f.slaPriority} onChange={(e) => setter({ ...f, slaPriority: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                {PRIORITIES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Max Hours *</label>
              <input type="number" value={f.slaMaxHours} onChange={(e) => setter({ ...f, slaMaxHours: e.target.value })}
                placeholder="e.g. 4" min="1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
            </div>
          </div>
        );
      case 'pattern':
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Ticket Count *</label>
              <input type="number" value={f.ticketCount} onChange={(e) => setter({ ...f, ticketCount: e.target.value })}
                placeholder="e.g. 5" min="1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Window Days *</label>
              <input type="number" value={f.windowDays} onChange={(e) => setter({ ...f, windowDays: e.target.value })}
                placeholder="e.g. 7" min="1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
            </div>
          </div>
        );
      case 'auto_close':
        return (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Resolved Hours *</label>
            <input type="number" value={f.resolvedHours} onChange={(e) => setter({ ...f, resolvedHours: e.target.value })}
              placeholder="e.g. 48" min="1"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
            <p className="mt-1 text-xs text-gray-400">Auto-close tickets resolved for this many hours</p>
          </div>
        );
      case 'unassigned':
        return (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Max Hours *</label>
            <input type="number" value={f.unassignedMaxHours} onChange={(e) => setter({ ...f, unassignedMaxHours: e.target.value })}
              placeholder="e.g. 2" min="1"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
            <p className="mt-1 text-xs text-gray-400">Trigger when ticket is unassigned for this long</p>
          </div>
        );
      default:
        return null;
    }
  };

  const filteredRules = search
    ? rules.filter(r => r.name.toLowerCase().includes(search.toLowerCase()) || r.triggerType.includes(search.toLowerCase()))
    : rules;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Escalation Rules</h1>
          <p className="text-sm text-gray-500">Configure automatic ticket escalation and notification rules</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleEvaluate}
            disabled={evaluating}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {evaluating ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            Evaluate Now
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors"
          >
            <Plus size={16} />
            Add Rule
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--saffron-50)]"><Shield size={20} className="text-[var(--ink-0)]" /></div>
              <div><p className="text-xs font-medium text-gray-500">Rules Active</p><p className="text-lg font-bold text-gray-900">{summary.rulesActive}</p></div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50"><TrendingUp size={20} className="text-red-600" /></div>
              <div><p className="text-xs font-medium text-gray-500">Escalated Today</p><p className="text-lg font-bold text-gray-900">{summary.escalatedToday}</p></div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50"><CheckCircle size={20} className="text-green-600" /></div>
              <div><p className="text-xs font-medium text-gray-500">Auto-Closed Today</p><p className="text-lg font-bold text-gray-900">{summary.autoClosedToday}</p></div>
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-50"><Clock size={20} className="text-yellow-600" /></div>
              <div><p className="text-xs font-medium text-gray-500">Pending Escalations</p><p className="text-lg font-bold text-gray-900">{summary.pendingEscalations}</p></div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search rules..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading escalation rules...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchRules} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <AlertTriangle size={40} className="mb-2" />
            <p className="text-sm">No escalation rules found</p>
            <p className="text-xs">Create rules to automate ticket escalation</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Trigger Type</th>
                  <th className="px-4 py-3">Conditions</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3 text-center">Active</th>
                  <th className="px-4 py-3 text-center">Priority</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRules.map(rule => (
                  <tr key={rule.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{rule.name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TRIGGER_COLORS[rule.triggerType] || 'bg-gray-100 text-gray-600'}`}>
                        {TRIGGER_LABELS[rule.triggerType] || rule.triggerType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-[200px]">
                      {conditionsSummary(rule.triggerType, rule.conditions)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_COLORS[rule.action] || 'bg-gray-100 text-gray-600'}`}>
                        {ACTION_LABELS[rule.action] || rule.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleActive(rule)}
                        className={`transition-colors ${rule.active ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-500'}`}
                        title={rule.active ? 'Deactivate' : 'Activate'}
                      >
                        {rule.active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{rule.priority}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditModal(rule)} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)]" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => handleDelete(rule)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Rule Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)} ariaLabel="Add Escalation Rule" className="!max-w-lg !bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Add Escalation Rule</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Critical SLA Breach Escalation"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Trigger Type *</label>
                  <select value={form.triggerType} onChange={(e) => setForm({ ...form, triggerType: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {TRIGGER_TYPES.map(t => <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Action *</label>
                  <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {ACTIONS.map(a => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
                  </select>
                </div>
              </div>

              {/* Dynamic Conditions */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-medium uppercase text-gray-500">Conditions</p>
                {renderConditionsForm(form, setForm)}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
                <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                  min="1" max="100"
                  className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                <p className="mt-1 text-xs text-gray-400">Lower number = higher priority</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleCreate} disabled={saving || !form.name}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />}
                Add Rule
              </button>
            </div>
        </Modal>
      )}

      {/* Edit Rule Modal */}
      {showEditModal && (
        <Modal open onClose={() => setShowEditModal(false)} ariaLabel="Edit Escalation Rule" className="!max-w-lg !bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit Escalation Rule</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name *</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Trigger Type *</label>
                  <select value={editForm.triggerType} onChange={(e) => setEditForm({ ...editForm, triggerType: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {TRIGGER_TYPES.map(t => <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Action *</label>
                  <select value={editForm.action} onChange={(e) => setEditForm({ ...editForm, action: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    {ACTIONS.map(a => <option key={a} value={a}>{ACTION_LABELS[a]}</option>)}
                  </select>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-medium uppercase text-gray-500">Conditions</p>
                {renderConditionsForm(editForm, setEditForm)}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
                <input type="number" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: Number(e.target.value) })}
                  min="1" max="100"
                  className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleEdit} disabled={editSaving || !editForm.name}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </button>
            </div>
        </Modal>
      )}

      {/* Evaluation Results Modal */}
      {showEvalResult && evalResult && (
        <Modal open onClose={() => setShowEvalResult(false)} ariaLabel="Evaluation Results" className="!max-w-md !bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Evaluation Results</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="px-6 py-4">
              <p className="mb-4 text-sm text-gray-600">
                <span className="font-semibold text-gray-900">{evalResult.triggered}</span> rule(s) triggered
              </p>
              {evalResult.details && evalResult.details.length > 0 ? (
                <div className="space-y-2">
                  {evalResult.details.map((d, i) => (
                    <div key={i} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <p className="text-sm font-medium text-gray-900">{d.ruleName}</p>
                      <p className="text-xs text-gray-500">
                        Action: <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ACTION_COLORS[d.action] || 'bg-gray-100 text-gray-600'}`}>{ACTION_LABELS[d.action] || d.action}</span>
                        {' '} &middot; {d.ticketsAffected} ticket(s) affected
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No rules were triggered. All tickets are within thresholds.</p>
              )}
            </div>
            <div className="flex items-center justify-end border-t border-gray-200 px-6 py-4">
              <RequestCloseButton className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]">Close</RequestCloseButton>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
